import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Loader2, Mail, Activity, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminApi, downloadCsv } from "./adminApi";

export default function LeadsAdmin() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const deliveries = useQuery({ queryKey: ["admin-deliveries"], queryFn: adminApi.deliveries });
  const leads = useQuery({ queryKey: ["admin-leads"], queryFn: adminApi.leads });
  const events = useQuery({ queryKey: ["admin-events", start, end], queryFn: () => adminApi.events(start || undefined, end ? end + "T23:59:59" : undefined) });

  const statusIcon = { sent: <CheckCircle2 className="h-4 w-4 text-emerald-400" />, failed: <XCircle className="h-4 w-4 text-rose-400" />, pending: <Clock className="h-4 w-4 text-amber-400" /> };

  return (
    <div data-testid="admin-leads-table">
      <h1 className="font-head text-3xl font-extrabold text-white">Leads & Analytics</h1>
      <p className="mt-1 text-slate-400">Document delivery logs and visitor interaction events.</p>

      <Tabs defaultValue="deliveries" className="mt-6">
        <TabsList className="bg-[#0b1730]">
          <TabsTrigger value="deliveries" data-testid="tab-deliveries">Document Deliveries</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Analytics Events</TabsTrigger>
        </TabsList>

        <TabsContent value="deliveries" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button variant="outline" className="border-slate-700" onClick={() => downloadCsv("/analytics/deliveries/export", "deliveries.csv")} data-testid="export-deliveries"><Download className="h-4 w-4" /> Export CSV</Button>
          </div>
          <div className="overflow-hidden rounded-2xl glass">
            {deliveries.isLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-cyan-400" /> :
              (deliveries.data || []).length === 0 ? <p className="p-8 text-center text-slate-500">No document delivery requests yet.</p> :
              <table className="w-full text-sm">
                <thead className="bg-[#050b18] text-left text-xs uppercase text-slate-400"><tr><th className="p-3">Date</th><th className="p-3">Email</th><th className="p-3">Document</th><th className="p-3">Status</th></tr></thead>
                <tbody>{deliveries.data.map((d) => (
                  <tr key={d.id} className="border-t border-slate-800"><td className="p-3 text-slate-400">{new Date(d.created_at).toLocaleString()}</td><td className="p-3 text-white">{d.email}</td><td className="p-3 text-slate-300">{d.doc_title}</td><td className="p-3"><span className="flex items-center gap-2 capitalize">{statusIcon[d.status]}{d.status}</span></td></tr>
                ))}</tbody>
              </table>}
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button variant="outline" className="border-slate-700" onClick={() => downloadCsv("/analytics/leads/export", "enquiries.csv")} data-testid="export-enquiries"><Download className="h-4 w-4" /> Export CSV</Button>
          </div>
          <div className="overflow-hidden rounded-2xl glass">
            {leads.isLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-cyan-400" /> :
              (leads.data || []).length === 0 ? <p className="p-8 text-center text-slate-500">No enquiries yet.</p> :
              <table className="w-full text-sm">
                <thead className="bg-[#050b18] text-left text-xs uppercase text-slate-400"><tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Mobile</th><th className="p-3">Email</th></tr></thead>
                <tbody>{leads.data.map((l) => (
                  <tr key={l.id} className="border-t border-slate-800"><td className="p-3 text-slate-400">{new Date(l.created_at).toLocaleString()}</td><td className="p-3 text-white">{l.name}</td><td className="p-3 text-slate-300">{l.mobile}</td><td className="p-3 text-slate-300">{l.email}</td></tr>
                ))}</tbody>
              </table>}
          </div>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div className="flex gap-2">
              <div><label className="text-xs text-slate-400">From</label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0b1730] border-slate-700" data-testid="events-start" /></div>
              <div><label className="text-xs text-slate-400">To</label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0b1730] border-slate-700" data-testid="events-end" /></div>
            </div>
            <Button variant="outline" className="border-slate-700" onClick={() => downloadCsv(`/analytics/events/export${start ? `?start=${start}` : ""}`, "events.csv")} data-testid="export-events"><Download className="h-4 w-4" /> Export CSV</Button>
          </div>
          <div className="overflow-hidden rounded-2xl glass">
            {events.isLoading ? <Loader2 className="m-8 h-6 w-6 animate-spin text-cyan-400" /> :
              (events.data || []).length === 0 ? <p className="p-8 text-center text-slate-500">No events in this range.</p> :
              <table className="w-full text-sm">
                <thead className="bg-[#050b18] text-left text-xs uppercase text-slate-400"><tr><th className="p-3">Date</th><th className="p-3">Event</th><th className="p-3">Detail</th></tr></thead>
                <tbody>{events.data.slice(0, 300).map((e) => (
                  <tr key={e.id} className="border-t border-slate-800"><td className="p-3 text-slate-400">{new Date(e.created_at).toLocaleString()}</td><td className="p-3 text-cyan-300">{e.type}</td><td className="p-3 text-slate-300">{e.label || "—"}</td></tr>
                ))}</tbody>
              </table>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
